import { Component, EventEmitter, Input, Output, OnChanges } from '@angular/core';
import { faFileImport } from '@fortawesome/free-solid-svg-icons';
import { FileSystemFileEntry, NgxFileDropEntry } from 'ngx-file-drop';

@Component({
  selector: 'file-upload',
  templateUrl: './file-upload.component.html'
})
export class FileUploadComponent implements OnChanges {

  @Input() id: string = "file-upload";
  @Input() allowMultiple: boolean = true;
  @Input() dropZoneLabel: string = "";
  @Input() buttonLabel: string = "Browse files";
  @Input() acceptedFileTypes: string[] = [];
  @Input() validationFailureMessage: string = "";
  /** Maximum allowed file size in megabytes (MB). Files larger than this size will be rejected. */
  @Input() maxFileSize: number;
  @Input() maxFiles: number;
  @Input() required = false;

  @Output() onAddedFilesSuccess = new EventEmitter<File[]>();

  public faFileImport = faFileImport;
  public importFiles: File[] = [];
  public errorMessage: string = "";

  public formCompleted = false;
  public hasValidated = false;

  // Cached computed properties (replacing getters)
  public failureMessage: string = '';
  public grammer: string = 'a';

  public get isMissingUpload(): boolean {
    // console.count('FileUploadComponent.isMissingUpload getter called');
    return this.importFiles.length === 0;
  }
  constructor() {
    this.updateCachedProperties();
  }

  ngOnChanges(): void {
    this.updateCachedProperties();
  }

  private updateCachedProperties(): void {
    // Update failureMessage when validationFailureMessage changes
    this.failureMessage = String(this.validationFailureMessage || '')?.trim();

    // Update grammer when dropZoneLabel changes
    if (this.dropZoneLabel && this.dropZoneLabel.length > 0) {
      let vowels = ("aeiouAEIOU");
      this.grammer = vowels.indexOf(this.dropZoneLabel[0]) !== -1 ? "an" : "a";
    } else {
      this.grammer = "a";
    }
  }

  public addFiles(files: NgxFileDropEntry[]): void {
    this.errorMessage = "";

    // If multiple files are not allowed and trying to add multiple files, add error message.
    if (!this.allowMultiple && (files.length > 1 || (this.importFiles.length > 0 && files.length > 0))) {
      this.errorMessage = `Cannot upload multiple files.`;
      return;
    }

    // If maximum amount of files specified && reached, add error message.
    if (this.maxFiles !== undefined && this.importFiles.length + files.length > this.maxFiles) {
      this.errorMessage = `Maximum amount of files is (${this.maxFiles}).`;
      return;
    }

    // Try adding each file to array.
    for (let file of files) {

      const fileEntry = file.fileEntry as FileSystemFileEntry;

      if (this.isValidFileType(fileEntry) && fileEntry.isFile) {

        // If filename already exists, overwrite the existing file.
        if (this.importFiles.some(f => f.name === file.fileEntry.name)) {
          this.importFiles = this.importFiles.filter(f => f.name !== file.fileEntry.name);
        }

        fileEntry.file((file: File) => {
          // Check file size if maxFileSize is defined
          if (this.maxFileSize !== undefined && this.maxFileSize !== null) {
            const fileSizeInMB = file.size / (1024 * 1024); // Convert bytes to MB
            if (fileSizeInMB > this.maxFileSize) {
              this.errorMessage = `File ${file.name} exceeds the maximum file size of ${this.maxFileSize}MB.`;
              return;
            }
          }
          this.importFiles.push(file);
        });
      }
      else {
        this.errorMessage = `File ${fileEntry.name} is not a valid file type.`;
        return;
      }
    }

    //emit files back to parent component
    this.onAddedFilesSuccess.emit(this.importFiles);
  }

  public removeFile(file: File): void {
    this.errorMessage = '';
    this.importFiles = this.importFiles.filter(f => f.name !== file.name);
  }

  public clearFiles(): void {
    this.errorMessage = '';
    this.importFiles = [];
  }

  //validates a file against the accepted file types array
  public isValidFileType(file: FileSystemFileEntry): boolean {

    let isValidFile: boolean = false;

    if (file) {
      if (this.acceptedFileTypes.length > 0) {
        const extensionRegEx = /(?:\.([^.]+))?$/;
        let fileExtension: string = extensionRegEx.exec(file.name)[1];

        if (this.acceptedFileTypes.indexOf(fileExtension) >= 0) {
          isValidFile = true;
        }
      }
      else {
        isValidFile = true;
      }
    }

    return isValidFile;
  }

  public validateForm(): void {
    this.hasValidated = true;
    this.formCompleted = !this.isMissingUpload;
  }
}
